// import components
import { Box, Chip, Typography } from '@mui/material';
import { TextField } from '@mui/material';

// import hooks
import { useTranslation } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form';

// import styles
import { Column, Route, Text, VerticalLine } from './TravelDetailStyles';

// import types
import {
  ExpenseClaimRouteResponse,
  TravelMovementResponse
} from 'generated/index';

// import utils
import { formatDistance, formatDistanceRaw, NumberIsFinite } from '../utils';
import {
  isTravelMovementResponse,
  isExpenseClaimRouteResponse
} from './TravelDetailDialogRoutes.utils';

type Props = {
  route: ExpenseClaimRouteResponse | TravelMovementResponse;
  isTravelMovements?: boolean;
  isLast: boolean;
  i: number;
  isManual?: boolean;
  isCorrected?: boolean;
  isEditMode?: boolean;
  editedDistance?: string;
  isRegisterPrivateKilometers?: boolean;
  onDistanceChange?: (index: number, value: string) => void;
};

const TravelDetailDiaglogRoutes = ({
  route,
  isTravelMovements,
  isLast,
  i,
  isManual,
  isEditMode,
  editedDistance,
  isRegisterPrivateKilometers,
  onDistanceChange
}: Props) => {
  const { t } = useTranslation('common');
  const { control } = useFormContext();

  const renderTravelMovementContent = (route: TravelMovementResponse) => (
    <>
      {isManual ? (
        <Typography variant="h6">{route.fromAddress}</Typography>
      ) : (
        <>
          <Typography variant="h6">{route.fromDescription}</Typography>
          <Text sx={{ textTransform: 'capitalize' }}>{route.fromAddress}</Text>
        </>
      )}
    </>
  );

  const renderExpenseClaimContent = (route: ExpenseClaimRouteResponse) => (
    <>
      <Typography variant="h6">{route.from}</Typography>
    </>
  );

  return (
    <Box key={i} ml={3} position="relative">
      <Route isTravelMovements={isTravelMovements}>
        <Column>
          {isTravelMovementResponse(route) &&
            renderTravelMovementContent(route)}
          {isExpenseClaimRouteResponse(route) &&
            renderExpenseClaimContent(route)}
        </Column>
        <VerticalLine
          isTravelMovements={isTravelMovements}
          isManual={isManual}
          isEditMode={isEditMode}
        />
      </Route>

      {route.distanceInMeters > 0 && (
        <Text
          sx={{
            position: 'relative',
            top: isTravelMovements ? '-12px' : '-15px',
            display: 'flex',
            alignItems: 'center',
            left: '2.5px',
            gap: '4px',
            lineHeight: 0
          }}
        >
          {isEditMode ? (
            <Controller
              name={`travelMovements[${i}].correctedDistanceInMeters`}
              control={control}
              render={() => (
                <TextField
                  label={t('travelMovements.distance')}
                  value={
                    editedDistance !== undefined
                      ? editedDistance
                      : NumberIsFinite(route.correctedDistanceInMeters)
                      ? formatDistanceRaw(route.correctedDistanceInMeters!)
                      : formatDistanceRaw(route.distanceInMeters)
                  }
                  onChange={(e) => onDistanceChange?.(i, e.target.value)}
                  type="number"
                  variant="outlined"
                  size="small"
                  sx={{
                    maxWidth: '100px',
                    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button':
                      {
                        WebkitAppearance: 'none',
                        margin: 0
                      },
                    '& input[type=number]': {
                      MozAppearance: 'textfield'
                    }
                  }}
                />
              )}
            />
          ) : (
            <>
              {NumberIsFinite(route.correctedDistanceInMeters) ? (
                <>{formatDistance(route.correctedDistanceInMeters!)}</>
              ) : (
                formatDistance(route.distanceInMeters)
              )}
            </>
          )}

          {(isTravelMovementResponse(route) ||
            (isExpenseClaimRouteResponse(route) &&
              isRegisterPrivateKilometers)) &&
            route.type !== null && (
              <Chip
                label={t(`travelMovements.type.${route.type}`)}
                size="small"
                variant="filled"
                color={route.type === 'PRIVATE' ? 'primary' : 'info'}
                sx={{
                  lineHeight: 'normal',
                  marginLeft: '4px'
                }}
              />
            )}

          {isTravelMovementResponse(route) &&
            NumberIsFinite(route.correctedDistanceInMeters) && (
              <Chip
                label={`${t('travelMovements.original')}: ${formatDistance(
                  route.distanceInMeters
                )}`}
                size="small"
                variant="filled"
                color="default"
                sx={{
                  lineHeight: 'normal',
                  marginLeft: '4px'
                }}
              />
            )}
        </Text>
      )}

      {isLast && (
        <Route isTravelMovements={isTravelMovements}>
          <Column>
            {isTravelMovementResponse(route) && !isManual ? (
              <Typography variant="h6">{route.toDescription}</Typography>
            ) : (
              isExpenseClaimRouteResponse(route) && (
                <Typography variant="h6">{route.to}</Typography>
              )
            )}
            {isTravelMovementResponse(route) && isManual && (
              <Typography variant="h6">{route.toAddress}</Typography>
            )}
            {isTravelMovementResponse(route) && !isManual && (
              <Text sx={{ textTransform: 'capitalize' }}>
                {route.toAddress}
              </Text>
            )}
          </Column>
          <VerticalLine
            isTravelMovements={isTravelMovements}
            isManual={isManual}
            isEditMode={isEditMode}
            $last={isLast}
          />
        </Route>
      )}
    </Box>
  );
};

export default TravelDetailDiaglogRoutes;
